import React, { useState, useRef, useEffect, useReducer } from 'react';
import { rollDice, parseDice, applyExhaustionToD20 } from '../utils/diceParser'; // [EXHAUSTION SYSTEM OCT 25, 2025]: D20 penalty application
import { effectCalculator } from '../services/effectCalculator';
import { systemBus, EVENTS } from '../services/systemBus';
import { shouldOfferInspiration, getInspirationThreshold } from '../utils/inspirationUtils'; // [PHASE 4B STEP 7 CONTINUED]: Comprehensive inspiration integration
import { getActiveCombatToggles, setActiveFlag, isActiveFlag } from '../data/combatToggles'; // [Phase 4b combat-panel UI - APR 2026]: Combat toggle flags
import applyOnSaveRolledTriggers from '../utils/applyOnSaveRolledTriggers'; // [SPEC C TASK 5 / Rule 22]: Shared on_save_rolled firer + advantage/consume application.
import consumeTriggeredSource from '../utils/consumeTriggeredSource'; // [SPEC C TASK 5]: Plumbs shouldConsume:true firings (Mind Sharpener pearl decrements).

const QuickActionsBar = ({
  character,
  setPlayerStats = null, // [Phase 4b combat-panel UI - APR 2026]: For combat-toggle flag flips
  inventory = [], // [NEW]: Required for effectCalculator
  settings,
  showStatus,
  addToActivityLog,
  activeQuickBonuses = [],
  isDemo = false,
  position = 'right', // 'left' or 'right' (legacy)
  savedPosition = null, // {x, y} for custom positioning
  onPositionChange = null, // Callback to save position
  setInspirationPrompt = null // [PHASE 4B STEP 7 CONTINUED]: For inspiration prompts
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [dragY, setDragY] = useState(0);
  const [tabPosition, setTabPosition] = useState(savedPosition || { x: window.innerWidth - 60, y: window.innerHeight / 2 });
  const [rollDC, setRollDC] = useState(''); // [PHASE 4B STEP 7 CONTINUED]: Optional DC for inspiration checks
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const barRef = useRef(null);

  // [PURPOSE]: Force re-render mechanism for event subscriptions
  // [WHY]: QuickActionsBar calculations use effectCalculator which caches results
  // [INTEGRATION]: When character state changes, cache must be cleared and component re-rendered
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // [PURPOSE]: Subscribe to all events that affect roll calculations
  // [WHY]: Saves, skills, and initiative depend on stats, feats, equipment, level, proficiency
  // [INTEGRATION]: systemBus events trigger cache clear + re-render for reactive updates
  useEffect(() => {
    const handleQuickActionsChange = (eventName, data) => {
      effectCalculator.clearCache(); // Clear cache to recalculate bonuses
      forceUpdate(); // Force re-render to show updated values
    };

    // Stat changes affect all rolls (saves, skills, initiative)
    const unsubscribeAbilityChanged = systemBus.on(EVENTS.ABILITY_SCORE_CHANGED, (data) =>
      handleQuickActionsChange('ABILITY_SCORE_CHANGED', data));
    const unsubscribeStatsChanged = systemBus.on(EVENTS.STATS_CHANGED, (data) =>
      handleQuickActionsChange('STATS_CHANGED', data));

    // Level changes affect proficiency bonus (saves, skills)
    const unsubscribeLevelUp = systemBus.on(EVENTS.LEVEL_UP, (data) =>
      handleQuickActionsChange('LEVEL_UP', data));

    // Class changes affect proficiency bonus and saving throw proficiencies
    const unsubscribeClassChanged = systemBus.on(EVENTS.CLASS_CHANGED, (data) =>
      handleQuickActionsChange('CLASS_CHANGED', data));

    // Feat changes affect bonuses (Alert feat +5 initiative, etc.)
    const unsubscribeFeatAdded = systemBus.on(EVENTS.FEAT_ADDED, (data) =>
      handleQuickActionsChange('FEAT_ADDED', data));
    const unsubscribeFeatRemoved = systemBus.on(EVENTS.FEAT_REMOVED, (data) =>
      handleQuickActionsChange('FEAT_REMOVED', data));

    // Equipment changes affect item bonuses (magic items give stat bonuses)
    const unsubscribeItemEquipped = systemBus.on(EVENTS.ITEM_EQUIPPED, (data) =>
      handleQuickActionsChange('ITEM_EQUIPPED', data));
    const unsubscribeItemUnequipped = systemBus.on(EVENTS.ITEM_UNEQUIPPED, (data) =>
      handleQuickActionsChange('ITEM_UNEQUIPPED', data));

    // Rest events affect hit dice availability
    const unsubscribeLongRest = systemBus.on(EVENTS.REST_LONG_COMPLETED, (data) =>
      handleQuickActionsChange('REST_LONG_COMPLETED', data));
    const unsubscribeShortRest = systemBus.on(EVENTS.REST_SHORT_COMPLETED, (data) =>
      handleQuickActionsChange('REST_SHORT_COMPLETED', data));

    // Skill changes affect which skills are proficient/expertise
    const unsubscribeSkillChanged = systemBus.on(EVENTS.SKILL_CHANGED, (data) =>
      handleQuickActionsChange('SKILL_CHANGED', data));

    // Race changes can affect stats (racial bonuses)
    const unsubscribeRaceChanged = systemBus.on(EVENTS.RACE_CHANGED, (data) =>
      handleQuickActionsChange('RACE_CHANGED', data));

    // Cleanup: Unsubscribe from all events on unmount
    return () => {
      unsubscribeAbilityChanged();
      unsubscribeStatsChanged();
      unsubscribeLevelUp();
      unsubscribeClassChanged();
      unsubscribeFeatAdded();
      unsubscribeFeatRemoved();
      unsubscribeItemEquipped();
      unsubscribeItemUnequipped();
      unsubscribeLongRest();
      unsubscribeShortRest();
      unsubscribeSkillChanged();
      unsubscribeRaceChanged();
    };
  }, []); // Empty deps - subscribe once on mount, cleanup on unmount

  // [EXHAUSTION SYSTEM OCT 25, 2025 FIX]: Helper to roll dice with advantage/disadvantage
  const rollDiceWithMode = (notation, mode = 'normal') => {
    const baseRoll = rollDice(notation);

    if (mode === 'normal') {
      return {
        ...baseRoll,
        mode: 'normal',
        allRolls: baseRoll.rolls
      };
    }

    // Roll a second d20 for advantage/disadvantage
    const secondRoll = rollDice(notation);

    if (mode === 'advantage') {
      const result = baseRoll.total >= secondRoll.total ? baseRoll.total : secondRoll.total;
      return {
        total: result,
        rolls: [result],
        allRolls: [baseRoll.total, secondRoll.total],
        mode: 'advantage',
        notation
      };
    } else if (mode === 'disadvantage') {
      const result = baseRoll.total <= secondRoll.total ? baseRoll.total : secondRoll.total;
      return {
        total: result,
        rolls: [result],
        allRolls: [baseRoll.total, secondRoll.total],
        mode: 'disadvantage',
        notation
      };
    }

    return {
      ...baseRoll,
      mode: 'normal',
      allRolls: baseRoll.rolls
    };
  };

  // Roll a saving throw
  const rollSavingThrow = (ability, abilityName) => {
    // [NEW]: Use effectCalculator for saving throw calculation
    // [WHY]: Central service for all stat calculations, includes all bonuses
    const saveBonus = effectCalculator.calculateSavingThrow(character, inventory, activeQuickBonuses, ability);
    const mod = saveBonus.breakdown.ability;
    const prof = saveBonus.breakdown.proficiency;
    const featBonus = saveBonus.breakdown.feat;
    const itemBonus = saveBonus.breakdown.item;

    // [EXHAUSTION SYSTEM OCT 25, 2025]: Apply exhaustion penalties to saving throws
    const exhaustionLevel = character.exhaustion || 0;
    const exhaustionRules = character.exhaustionRules || settings.exhaustionRules || '5e';
    const exhaustionEffect = applyExhaustionToD20(exhaustionLevel, exhaustionRules, 'saving_throw', 'normal');

    // [PHASE 6D]: Check for condition-based disadvantage or auto-failure
    const conditionEffect = effectCalculator.hasDisadvantageOnSavingThrow(character, ability);
    let finalRollMode = exhaustionEffect.mode;
    let conditionNote = '';

    if (conditionEffect === 'auto-fail') {
      // Some conditions cause automatic failure on certain saves
      showStatus(`❌ Automatically failed ${abilityName} save due to condition!`, 3000);

      if (addToActivityLog) {
        addToActivityLog({
          type: 'save',
          message: `Failed ${abilityName} save (auto-fail)`,
          result: 'Auto-Fail',
          breakdown: 'Condition causes automatic failure',
          timestamp: new Date().toISOString()
        });
      }
      return; // Exit early for auto-fail
    } else if (conditionEffect && finalRollMode === 'normal') {
      // Apply disadvantage from conditions
      finalRollMode = 'disadvantage';
      conditionNote = ' (condition)';
    } else if (conditionEffect && finalRollMode === 'advantage') {
      // Disadvantage cancels advantage
      finalRollMode = 'normal';
      conditionNote = ' (condition cancels advantage)';
    }

    // [SPEC C TASK 5 — 2026-05-17]: Fire on_save_rolled triggers BEFORE the
    //   d20 roll so advantage payloads (Mind Sharpener: advantage on CON
    //   concentration save) can flip the in-flight rollMode. The util
    //   returns { finalMode, triggeredLogEntries, pendingConsumes }. mode
    //   replaces finalRollMode; pendingConsumes are plumbed AFTER the roll
    //   so charges only decrement when the trigger actually fired.
    const saveTriggerResult = applyOnSaveRolledTriggers(character, inventory, {
      ability,
      mode: finalRollMode,
    });
    finalRollMode = saveTriggerResult.finalMode;

    // [EXHAUSTION SYSTEM OCT 25, 2025 FIX]: Roll with advantage/disadvantage support
    const rollResult = rollDiceWithMode('1d20', finalRollMode);
    const roll = rollResult.rolls[0]; // The actual d20 result used
    let total = roll + saveBonus.total + exhaustionEffect.penalty;

    // Build breakdown with advantage/disadvantage info
    let breakdownParts = [];
    if (rollResult.mode === 'advantage') {
      breakdownParts.push(`d20(${rollResult.allRolls[0]}, ${rollResult.allRolls[1]}) advantage → ${roll}`);
    } else if (rollResult.mode === 'disadvantage') {
      breakdownParts.push(`d20(${rollResult.allRolls[0]}, ${rollResult.allRolls[1]}) disadvantage → ${roll}`);
    } else {
      breakdownParts.push(`d20(${roll})`);
    }
    breakdownParts.push(`${mod} (${abilityName})`);
    if (prof) breakdownParts.push(`${prof} (prof)`);
    if (featBonus) breakdownParts.push(`${featBonus} (feat)`);
    if (itemBonus) breakdownParts.push(`${itemBonus} (item)`); // [NEW]: Item bonuses from magic items

    // Apply quick bonuses
    activeQuickBonuses.forEach(bonus => {
      if (bonus.affects?.includes('saves')) {
        if (bonus.dice) {
          const bonusRoll = rollDice(bonus.dice);
          total += bonusRoll.total;
          breakdownParts.push(`${bonusRoll.total} (${bonus.name})`);
        } else if (bonus.value) {
          total += bonus.value;
          breakdownParts.push(`${bonus.value} (${bonus.name})`);
        }
      }
    });

    // Add exhaustion penalty to breakdown if present
    if (exhaustionEffect.penalty !== 0) {
      breakdownParts.push(`${exhaustionEffect.penalty} (exhaustion)`);
    }

    // [PHASE 6D FIX]: Add condition note to breakdown for user visibility
    if (conditionNote) {
      breakdownParts.push(conditionNote.trim());
    }

    const breakdown = breakdownParts.join(' + ');

    // Add exhaustion and advantage/disadvantage info to status message
    let statusMessage = `${abilityName} Save: ${total}`;
    if (rollResult.mode === 'advantage') {
      statusMessage += ' (Advantage)';
    } else if (rollResult.mode === 'disadvantage') {
      statusMessage += ' (Disadvantage)';
    }
    statusMessage += ` [${breakdown}]`;
    if (exhaustionEffect.description) {
      statusMessage += ` - ${exhaustionEffect.description}`;
    }

    showStatus(statusMessage, 3000);

    if (addToActivityLog) {
      addToActivityLog({
        type: 'saving_throw',
        message: `${abilityName} saving throw`,
        result: total,
        breakdown,
        // [SPEC C TASK 5 — 2026-05-17]: triggeredOnSave attaches firing details
        //   (sourceLabel + kind + payload) so consumers can render which
        //   triggeredEffect contributed to the mode flip / log lines.
        triggeredOnSave: saveTriggerResult.triggeredLogEntries.length > 0
          ? saveTriggerResult.triggeredLogEntries
          : undefined,
        timestamp: new Date().toISOString()
      });
    }

    // [SPEC C TASK 5 — 2026-05-17]: Plumb shouldConsume:true firings.
    //   Each pendingConsumes entry is a sourceRef ({type, id, classKey?,
    //   infusionName?}). consumeTriggeredSource dispatches on .type:
    //   'infusion' decrements bound infusion charges (Mind Sharpener pearl
    //   pattern). 'item' would decrement a stack but item-on-save-rolled
    //   is not a v1 SRD pattern. removeItem is not available in
    //   QuickActionsBar's prop set so we pass setPlayerStats only — item
    //   consumes on a save trigger are an exotic case left for follow-up
    //   when an SRD example surfaces.
    for (const sourceRef of saveTriggerResult.pendingConsumes) {
      consumeTriggeredSource(sourceRef, { setPlayerStats });
    }

    // [PHASE 4B STEP 7 CONTINUED]: Check if inspiration should be offered
    // [WHY]: If saving throw failed within threshold of DC, prompt player to use inspiration
    // [D&D 5e RULE]: Inspiration can be used on saving throws
    if (rollDC && rollDC !== '' && setInspirationPrompt) {
      const dc = parseInt(rollDC);
      const inspirationCount = character.inspiration || 0;
      const threshold = getInspirationThreshold(settings);

      if (shouldOfferInspiration(total, dc, inspirationCount, threshold)) {
        const failureMargin = dc - total;
        setInspirationPrompt({
          result: total,
          dc: dc,
          failureMargin: failureMargin,
          rollType: 'saving throw',
          description: `${abilityName} Save`,
          onReroll: () => rollSavingThrow(ability, abilityName) // Reroll same save
        });
      }
    }
  };

  // Roll a skill check
  const rollSkillCheck = (skillName, ability) => {
    // [NEW]: Use effectCalculator for skill bonus calculation
    // [WHY]: Central service for all stat calculations, includes all bonuses
    const skillKey = skillName; // Already using skill name as key
    const profLevel = character.skills?.[skillName] || 0;
    const skillBonus = effectCalculator.calculateSkillBonus(
      character,
      inventory,
      activeQuickBonuses,
      skillKey,
      ability,
      profLevel
    );
    const mod = skillBonus.breakdown.ability;
    const prof = skillBonus.breakdown.proficiency;
    const featBonus = skillBonus.breakdown.feat;
    const jackOfAllTrades = skillBonus.breakdown.jackOfAllTrades || 0;

    const roll = rollDice('1d20');
    let total = roll.total + skillBonus.total;

    const profText = skillBonus.level === 2 ? 'expertise' : skillBonus.level === 1 ? 'prof' : '';
    let breakdownParts = [`d20(${roll.rolls[0]})`, `${mod} (${ability})`];
    if (prof && profText) breakdownParts.push(`${prof} (${profText})`);
    if (featBonus) breakdownParts.push(`${featBonus} (feat)`);
    if (jackOfAllTrades) breakdownParts.push(`${jackOfAllTrades} (Jack of All Trades)`);

    // Apply quick bonuses (Guidance affects ability checks)
    activeQuickBonuses.forEach(bonus => {
      if (bonus.affects?.includes('ability_checks')) {
        if (bonus.dice) {
          const bonusRoll = rollDice(bonus.dice);
          total += bonusRoll.total;
          breakdownParts.push(`${bonusRoll.total} (${bonus.name})`);
        } else if (bonus.value) {
          total += bonus.value;
          breakdownParts.push(`${bonus.value} (${bonus.name})`);
        }
      }
    });

    const breakdown = breakdownParts.join(' + ');

    showStatus(
      `${skillName}: ${total} [${breakdown}]`,
      3000
    );

    if (addToActivityLog) {
      addToActivityLog({
        type: 'skill_check',
        message: skillName,
        result: total,
        breakdown,
        timestamp: new Date().toISOString()
      });
    }

    // [PHASE 4B STEP 7 CONTINUED]: Check if inspiration should be offered
    // [WHY]: If skill check failed within threshold of DC, prompt player to use inspiration
    // [D&D 5e RULE]: Inspiration can be used on ability checks (which includes skill checks)
    if (rollDC && rollDC !== '' && setInspirationPrompt) {
      const dc = parseInt(rollDC);
      const inspirationCount = character.inspiration || 0;
      const threshold = getInspirationThreshold(settings);

      if (shouldOfferInspiration(total, dc, inspirationCount, threshold)) {
        const failureMargin = dc - total;
        setInspirationPrompt({
          result: total,
          dc: dc,
          failureMargin: failureMargin,
          rollType: 'ability check',
          description: `${skillName} (${ability.toUpperCase()})`,
          onReroll: () => rollSkillCheck(skillName, ability) // Reroll same check
        });
      }
    }
  };

  // Roll initiative
  const rollInitiative = () => {
    // [NEW]: Use effectCalculator for initiative calculation
    // [WHY]: Central service for all stat calculations, includes all bonuses (Alert feat, etc.)
    const initiativeBonus = effectCalculator.calculateInitiative(character, inventory, activeQuickBonuses);

    const roll = rollDice('1d20');
    let total = roll.total + initiativeBonus;

    let breakdownParts = [`d20(${roll.rolls[0]})`, `${initiativeBonus} (initiative)`];

    // Apply quick bonuses that affect initiative (it's an ability check)
    activeQuickBonuses.forEach(bonus => {
      if (bonus.affects?.includes('ability_checks') || bonus.affects?.includes('initiative')) {
        if (bonus.dice) {
          const bonusRoll = rollDice(bonus.dice);
          total += bonusRoll.total;
          breakdownParts.push(`${bonusRoll.total} (${bonus.name})`);
        } else if (bonus.value) {
          total += bonus.value;
          breakdownParts.push(`${bonus.value} (${bonus.name})`);
        }
      }
    });

    const breakdown = breakdownParts.join(' + ');

    showStatus(
      `Initiative: ${total} [${breakdown}]`,
      3000
    );

    if (addToActivityLog) {
      addToActivityLog({
        type: 'combat', // [A3 FIX]: Initiative is a combat action, not a generic roll
        message: 'Initiative',
        result: total,
        breakdown,
        timestamp: new Date().toISOString()
      });
    }

    // [PHASE 4B STEP 7 CONTINUED]: Check if inspiration should be offered
    // [WHY]: Initiative is a Dexterity ability check, so inspiration can be used
    // [D&D 5e RULE]: Inspiration can be used on ability checks (including initiative)
    if (rollDC && rollDC !== '' && setInspirationPrompt) {
      const dc = parseInt(rollDC);
      const inspirationCount = character.inspiration || 0;
      const threshold = getInspirationThreshold(settings);

      if (shouldOfferInspiration(total, dc, inspirationCount, threshold)) {
        const failureMargin = dc - total;
        setInspirationPrompt({
          result: total,
          dc: dc,
          failureMargin: failureMargin,
          rollType: 'ability check',
          description: 'Initiative (DEX)',
          onReroll: () => rollInitiative() // Reroll initiative
        });
      }
    }
  };

  // Quick heal (hit dice)
  // [BUG FIX MAY 2026 / #201]: Pre-fix, this calculated `healing` and showed
  //   the toast but NEVER called setPlayerStats — so HP did not increase and
  //   the hit-die pool did not decrement. Cosmetic-only. Also: legacy code
  //   read `character.hitDice.current` / `.type` (flat shape), but the
  //   canonical schema is `character.hitDice[dieType] = { current, max }`
  //   keyed by die type — so the original guard was always falsy in modern
  //   data, falling through to a 1d8 default + show-only toast.
  //
  //   Now: pick the largest available die pool (d12 → d10 → d8 → d6), apply
  //   the heal capped at maxHitPoints, decrement that pool, and emit
  //   HP_CHANGED. Mirrors RestPanel.handleRollHitDice for parity.
  const useHitDice = () => {
    if (!setPlayerStats) {
      showStatus('Hit dice unavailable in this view', 2000);
      return;
    }

    const hitDiceMap = character?.hitDice || {};
    const dieOrder = ['d12', 'd10', 'd8', 'd6'];
    const diceType = dieOrder.find(t => (hitDiceMap[t]?.current ?? 0) > 0);

    if (!diceType) {
      showStatus('No hit dice remaining!', 2000);
      return;
    }

    const effectiveConMod = effectCalculator.getEffectiveModifier(character, inventory, activeQuickBonuses, 'constitution');
    const roll = rollDice(`1${diceType}`);
    // PHB p.186 — minimum 1 HP per hit-die spend even if con mod is negative
    const healing = Math.max(1, roll.total + effectiveConMod);
    const breakdown = `${diceType}(${roll.rolls[0]}) + ${effectiveConMod} (CON)`;

    const oldHP = character?.currentHitPoints ?? 0;
    const maxHP = character?.maxHitPoints ?? oldHP;

    setPlayerStats(prev => {
      if (!prev.hitDice?.[diceType] || prev.hitDice[diceType].current <= 0) return prev;
      const prevHP = prev.currentHitPoints ?? 0;
      const cappedHP = Math.min(prevHP + healing, prev.maxHitPoints ?? prevHP);
      return {
        ...prev,
        currentHitPoints: cappedHP,
        hitDice: {
          ...prev.hitDice,
          [diceType]: {
            ...prev.hitDice[diceType],
            current: prev.hitDice[diceType].current - 1
          }
        }
      };
    });

    systemBus.emit(EVENTS.HP_CHANGED, {
      oldHP,
      newHP: Math.min(oldHP + healing, maxHP),
      maxHP,
      damage: 0,
      healing: Math.min(maxHP - oldHP, healing),
      source: 'hit_dice_quick'
    });

    showStatus(
      `Hit Dice Healing: ${healing} HP [${breakdown}]`,
      3000
    );

    if (addToActivityLog) {
      addToActivityLog({
        type: 'healing',
        message: 'Used Hit Dice',
        result: healing,
        breakdown,
        timestamp: new Date().toISOString()
      });
    }
  };

  // [PURPOSE]: Unified drag handling for both touch (mobile) and mouse (desktop)
  // [WHY]: Original implementation only had touch handlers, making it undraggable on desktop
  const hasDraggedRef = useRef(false);

  // --- Touch drag (mobile) ---
  const handleTabTouchStart = (e) => {
    e.stopPropagation();
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    dragStartPos.current = { x: tabPosition.x, y: tabPosition.y };
    hasDraggedRef.current = false;
    setIsDragging(true);
  };

  const handleTabTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();

    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;

    // Only count as drag if moved more than 5px (prevents accidental drag on tap)
    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      hasDraggedRef.current = true;
    }

    const newX = dragStartPos.current.x + deltaX;
    const newY = dragStartPos.current.y + deltaY;
    const boundedX = Math.max(10, Math.min(window.innerWidth - 60, newX));
    const boundedY = Math.max(10, Math.min(window.innerHeight - 60, newY));

    setTabPosition({ x: boundedX, y: boundedY });
  };

  const handleTabTouchEnd = () => {
    setIsDragging(false);
    if (onPositionChange && hasDraggedRef.current) {
      onPositionChange(tabPosition);
    }
  };

  // --- Mouse drag (desktop) ---
  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    touchStartX.current = e.clientX;
    touchStartY.current = e.clientY;
    dragStartPos.current = { x: tabPosition.x, y: tabPosition.y };
    hasDraggedRef.current = false;
    setIsDragging(true);

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - touchStartX.current;
      const deltaY = moveEvent.clientY - touchStartY.current;

      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        hasDraggedRef.current = true;
      }

      const newX = dragStartPos.current.x + deltaX;
      const newY = dragStartPos.current.y + deltaY;
      const boundedX = Math.max(10, Math.min(window.innerWidth - 60, newX));
      const boundedY = Math.max(10, Math.min(window.innerHeight - 60, newY));

      setTabPosition({ x: boundedX, y: boundedY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      // Position save is handled by the isDragging effect below
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // [PURPOSE]: Save position after mouse drag ends (state is up-to-date in effect)
  const prevDragging = useRef(false);
  useEffect(() => {
    if (prevDragging.current && !isDragging && hasDraggedRef.current && onPositionChange) {
      onPositionChange(tabPosition);
    }
    prevDragging.current = isDragging;
  }, [isDragging]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle click to toggle (only if not dragging)
  const handleTabClick = () => {
    if (!hasDraggedRef.current) {
      setIsOpen(!isOpen);
    }
  };

  // Common skills for quick access
  const commonSkills = [
    { name: 'Perception', ability: 'wis' },
    { name: 'Investigation', ability: 'int' },
    { name: 'Stealth', ability: 'dex' },
    { name: 'Athletics', ability: 'str' },
    { name: 'Acrobatics', ability: 'dex' },
    { name: 'Insight', ability: 'wis' }
  ];

  // [P1.7 APR 2026]: Per-action visibility for the Quick Actions bar.
  // [WHY]: Users can hide individual sections (Initiative, individual saves,
  //   individual skills, Hit Dice, Quick Dice, Combat Toggles) to streamline
  //   their bar. Defaults to all-visible — undefined flags are treated as
  //   visible for backwards-compat with users who upgraded from a build
  //   without this setting.
  // [SHAPE]: settings.quickActionsVisibility = {
  //   initiative, combatToggles, saves: {str,...}, skills: {Perception,...},
  //   hitDice, quickDice
  // }
  const visibility = settings?.quickActionsVisibility || {};

  return (
    <>
      {/* Draggable Tab to open/close */}
      <div
        className={`fixed z-40 cursor-move touch-none`}
        style={{
          left: `${tabPosition.x}px`,
          top: `${tabPosition.y}px`,
          transform: 'translate(-50%, -50%)',
          transition: isDragging ? 'none' : 'all 0.2s ease'
        }}
        onClick={handleTabClick}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTabTouchStart}
        onTouchMove={handleTabTouchMove}
        onTouchEnd={handleTabTouchEnd}
      >
        <div className={`bg-gray-700 p-3 rounded-lg shadow-lg ${isDragging ? 'scale-110' : ''} transition-transform`}
             title={isOpen ? 'Close Quick Actions' : 'Quick Actions'}>
          <div className="text-white text-2xl">
            {isOpen ? '✖' : '⚡'}
          </div>
        </div>
      </div>

      {/* Quick Actions Panel - Opens near the tab */}
      {isOpen && (
        <div
          ref={barRef}
          className={`fixed bg-gray-800 shadow-2xl z-30 w-64 max-h-96 overflow-y-auto rounded-lg`}
          style={{
            left: tabPosition.x < window.innerWidth / 2 ? `${tabPosition.x + 40}px` : 'auto',
            right: tabPosition.x >= window.innerWidth / 2 ? `${window.innerWidth - tabPosition.x + 40}px` : 'auto',
            top: `${Math.max(10, Math.min(window.innerHeight - 400, tabPosition.y - 50))}px`
          }}
        >
        <div className="p-4">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center">
            <span className="mr-2">⚡</span> Quick Actions
          </h3>

          {/* [PHASE 4B STEP 7 CONTINUED]: DC Input for Inspiration Checks */}
          {/* [WHY]: Optional DC allows inspiration auto-prompt on failed rolls within threshold */}
          {/* [FUTURE]: DM can push DC from party panel to all players */}
          <div className="mb-4 pb-4 border-b border-gray-600">
            <label htmlFor="quickActionsDC" className="text-xs text-gray-400 block mb-1">
              DC (optional):
            </label>
            <div className="flex items-center gap-2">
              <input
                id="quickActionsDC"
                type="number"
                min="1"
                max="30"
                value={rollDC}
                onChange={(e) => setRollDC(e.target.value)}
                placeholder="15"
                className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:border-purple-500 focus:outline-none"
                title="Set DC to enable inspiration prompts on close failures"
              />
              {rollDC && (
                <button
                  onClick={() => setRollDC('')}
                  className="text-xs text-gray-500 hover:text-gray-300 px-2"
                  title="Clear DC"
                >
                  ✕
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Set DC to trigger inspiration prompts
            </p>
          </div>

          {/* Initiative */}
          {/* [P1.7]: Honor visibility.initiative — undefined treated as visible. */}
          {visibility.initiative !== false && (
            <div className="mb-4">
              <h4 className="text-sm text-gray-400 mb-2">Combat</h4>
              <button
                onClick={rollInitiative}
                disabled={isDemo}
                className="w-full p-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600
                  text-white rounded transition-colors mb-2"
              >
                🎲 Roll Initiative
              </button>
            </div>
          )}

          {/* [Phase 4b combat-panel UI — APR 2026]: Combat Toggles section.
              Surfaces character-relevant per-attack/per-cast flags from
              playerStats._activeFlags. The data layer (Bucket 2/3 wiring)
              already declares conditional effects against these flags via
              _buildContext; flipping a flag here makes the next calculator
              call honor the condition. Transient-only (no autosave) — flags
              reset on page reload, matching D&D's per-attack semantics.
              [P1.7]: Visibility-gated via settings.quickActionsVisibility.combatToggles. */}
          {visibility.combatToggles !== false && (() => {
            const activeToggles = getActiveCombatToggles(character);
            if (!setPlayerStats || activeToggles.length === 0 || isDemo) return null;
            return (
              <div className="mb-4">
                <h4 className="text-sm text-gray-400 mb-2 flex items-center gap-2">
                  <span>🎚️</span>
                  <span>Combat Toggles</span>
                  <span className="text-xs text-gray-500" title="Toggle on before rolling. Flags reset on page reload.">(per-attack)</span>
                </h4>
                <div className="space-y-1">
                  {activeToggles.map(toggle => {
                    const active = isActiveFlag(character, toggle.flag);
                    return (
                      <button
                        key={toggle.flag}
                        type="button"
                        onClick={() => {
                          setPlayerStats(prev => setActiveFlag(prev, toggle.flag));
                          // Clear cache so next read reflects the new flag value.
                          effectCalculator.clearCache();
                        }}
                        title={toggle.description}
                        className={`w-full px-2 py-1.5 rounded text-left text-xs transition-colors flex items-center gap-2 ${
                          active
                            ? 'bg-amber-600 hover:bg-amber-700 text-white border border-amber-400'
                            : 'bg-gray-700 hover:bg-gray-600 text-gray-300 border border-gray-600'
                        }`}
                      >
                        <span className="text-base">{toggle.icon}</span>
                        <span className="flex-1">{toggle.name}</span>
                        <span className={`text-xs font-mono ${active ? 'text-amber-200' : 'text-gray-500'}`}>
                          {active ? 'ON' : 'OFF'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Saving Throws */}
          {/* [P1.7]: Filter per-save by visibility.saves[stat]. If every
              save is hidden, suppress the whole section header. */}
          {(() => {
            const SAVES = {
              str: 'STR', dex: 'DEX', con: 'CON',
              int: 'INT', wis: 'WIS', cha: 'CHA'
            };
            const visibleSaves = Object.entries(SAVES).filter(
              ([stat]) => visibility.saves?.[stat] !== false
            );
            if (visibleSaves.length === 0) return null;
            return (
              <div className="mb-4">
                <h4 className="text-sm text-gray-400 mb-2">Saving Throws</h4>
                <div className="grid grid-cols-2 gap-2">
                  {visibleSaves.map(([stat, name]) => {
                    const isProficient = character.proficiencies?.savingThrows?.includes(stat);
                    return (
                      <button
                        key={stat}
                        onClick={() => rollSavingThrow(stat, name)}
                        disabled={isDemo}
                        className={`p-2 rounded transition-colors text-white text-sm
                          ${isProficient
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-gray-600 hover:bg-gray-700'}
                          disabled:bg-gray-700`}
                      >
                        {name} {isProficient && '✓'}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Common Skills */}
          {/* [P1.7]: Filter per-skill by visibility.skills[name]. If every
              skill is hidden, suppress the whole section header. */}
          {(() => {
            const visibleSkills = commonSkills.filter(
              skill => visibility.skills?.[skill.name] !== false
            );
            if (visibleSkills.length === 0) return null;
            return (
              <div className="mb-4">
                <h4 className="text-sm text-gray-400 mb-2">Common Skills</h4>
                <div className="space-y-2">
                  {visibleSkills.map(skill => {
                    const skillLevel = character.skills?.[skill.name] || 0;
                    return (
                      <button
                        key={skill.name}
                        onClick={() => rollSkillCheck(skill.name, skill.ability)}
                        disabled={isDemo}
                        className={`w-full p-2 rounded transition-colors text-white text-sm text-left
                          ${skillLevel === 2
                            ? 'bg-purple-600 hover:bg-purple-700'
                            : skillLevel === 1
                            ? 'bg-blue-600 hover:bg-blue-700'
                            : 'bg-gray-600 hover:bg-gray-700'}
                          disabled:bg-gray-700`}
                      >
                        {skill.name}
                        {skillLevel === 2 && ' (E)'}
                        {skillLevel === 1 && ' (P)'}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Quick Healing */}
          {/* [P1.7]: Honor visibility.hitDice — undefined treated as visible. */}
          {/* [2026-05-11 FIX]: character.hitDice is the canonical MAP schema
              (e.g., { d8: { current, max }, d6: { current, max } }) per
              CharacterCreationWizard.js + rechargeTypes.js. Pre-fix the
              button read character.hitDice?.current (flat shape) which is
              always undefined under the map schema — button was permanently
              disabled and showed 0/0 even when the character had dice. */}
          {visibility.hitDice !== false && (() => {
            const hitDiceMap = character.hitDice || {};
            const totalCurrent = Object.values(hitDiceMap)
              .reduce((sum, die) => sum + (die?.current ?? 0), 0);
            const totalMax = Object.values(hitDiceMap)
              .reduce((sum, die) => sum + (die?.max ?? 0), 0);
            return (
              <div className="mb-4">
                <h4 className="text-sm text-gray-400 mb-2">Recovery</h4>
                <button
                  onClick={useHitDice}
                  disabled={isDemo || totalCurrent === 0}
                  className="w-full p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600
                    text-white rounded transition-colors"
                >
                  💚 Use Hit Dice ({totalCurrent}/{totalMax})
                </button>
              </div>
            );
          })()}

          {/* Quick Dice Roller */}
          {/* [P1.7]: Honor visibility.quickDice — undefined treated as visible. */}
          {visibility.quickDice !== false && (
            <div className="mb-4">
              <h4 className="text-sm text-gray-400 mb-2">Quick Dice</h4>
              <div className="grid grid-cols-3 gap-2">
                {['d4', 'd6', 'd8', 'd10', 'd12', 'd20'].map(die => (
                  <button
                    key={die}
                    onClick={() => {
                      const roll = rollDice(`1${die}`);
                      showStatus(`${die}: ${roll.total}`, 2000);

                      // Log to activity feed
                      if (addToActivityLog) {
                        addToActivityLog({
                          type: 'roll',
                          message: `Quick ${die} roll`,
                          result: roll.total,
                          breakdown: `${die}(${roll.rolls[0]})`,
                          timestamp: new Date().toISOString()
                        });
                      }
                    }}
                    disabled={isDemo}
                    className="p-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-700
                      text-white rounded transition-colors text-sm"
                  >
                    {die}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Demo Mode Notice */}
          {isDemo && (
            <div className="mt-4 p-2 bg-yellow-600 rounded text-white text-xs">
              Demo Mode - Actions Disabled
            </div>
          )}
        </div>
      </div>
      )}
    </>
  );
};

export default QuickActionsBar;